import React from 'react';

import { UseFormReturn } from 'react-hook-form';

import {
  Company,
  CompanyCreateApiDTO,
  CompanyUpdateApiDTO,
  ConstantArray,
  isServiceTitanPortalV2Company,
  isStCapacityCompany,
  Paths,
  PathValue
} from '_lib/api/apiTypes.shim';
import { PrimaryCardHeader } from '_lib/components/CardHeader/CardHeader';
import { CheckboxFormItem } from '_lib/components/CheckboxFormItem/CheckboxFormItem';
import { FlexCard } from '_lib/components/FlexCard/FlexCard';
import { FormHelperText } from '_lib/components/FormHelperText/FormHelperText';
import { Stack } from '_lib/components/Stack/Stack';
import { PhoneNumberTextFieldFormItem, TextFieldFormItem } from '_lib/components/TextFieldFormItem/TextFieldFormItem';
import { useMediaQuery } from '_lib/hooks/useMediaQuery';

import { FlexStack } from '../FlexStack/FlexStack';

export const companyContactsFormValues = [
  'primaryContact.name',
  'primaryContact.role',
  'primaryContact.phoneNumber',
  'primaryContact.email',
  'primaryContact.notes',
  'secondaryContact.name',
  'secondaryContact.role',
  'secondaryContact.phoneNumber',
  'secondaryContact.email',
  'secondaryContact.notes',
  'onCallNotes',
  'isAfterHoursReviewEnabled',
  'contactScheduleUrl'
] satisfies ConstantArray<Paths<CompanyUpdateApiDTO | CompanyCreateApiDTO>>;

export type CompanyContactsFormValue = (typeof companyContactsFormValues)[number];

export type CompanyContactsFormValues = {
  [K in CompanyContactsFormValue]: PathValue<CompanyUpdateApiDTO | CompanyCreateApiDTO, K>;
};

/**
 * Form is only responsible for fields it controls, and we can check for the generic constraint in the
 * props in order for this to be reusable.
 *
 * See https://github.com/orgs/react-hook-form/discussions/8311#discussioncomment-9759698
 */
type CompanyContactsFormProps<T extends CompanyContactsFormValues> = {
  companyContactsFormContext: T extends CompanyContactsFormValues ? UseFormReturn<T> : never;
  company?: Company;
};

export const CompanyContactsForm = <T extends CompanyContactsFormValues>(props: CompanyContactsFormProps<T>) => {
  const { companyContactsFormContext, company } = props;
  const { control } = companyContactsFormContext;

  const singleColumn = useMediaQuery((theme) => theme.breakpoints.only('xs'));

  return (
    <Stack useFlexGap spacing={2} sx={{ width: '100%' }}>
      <FlexCard>
        <PrimaryCardHeader title='Primary Contact' />
        <Stack useFlexGap sx={{ padding: 2, width: '100%' }}>
          <FlexStack singleColumn={singleColumn}>
            <TextFieldFormItem
              name='primaryContact.name'
              label='Name'
              control={control}
              multiline
              maxRows={3}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <TextFieldFormItem
              name='primaryContact.role'
              label='Role'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
          </FlexStack>
          <FlexStack singleColumn={singleColumn}>
            <PhoneNumberTextFieldFormItem
              name='primaryContact.phoneNumber'
              label='Phone Number'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <TextFieldFormItem
              name='primaryContact.email'
              label='Email'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
          </FlexStack>
          <TextFieldFormItem
            name='primaryContact.notes'
            label='Contact Instructions'
            control={control}
            multiline
            minRows={2}
            maxRows={10}
            fullWidth
            disabled={!!company && !company.isActive}
          />
        </Stack>
      </FlexCard>
      <FlexCard>
        <PrimaryCardHeader title='Secondary Contact' />
        <Stack useFlexGap sx={{ padding: 2, width: '100%' }}>
          <FlexStack singleColumn={singleColumn}>
            <TextFieldFormItem
              name='secondaryContact.name'
              label='Name'
              control={control}
              multiline
              maxRows={3}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <TextFieldFormItem
              name='secondaryContact.role'
              label='Role'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
          </FlexStack>
          <FlexStack singleColumn={singleColumn}>
            <PhoneNumberTextFieldFormItem
              name='secondaryContact.phoneNumber'
              label='Phone Number'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            <TextFieldFormItem
              name='secondaryContact.email'
              label='Email'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
          </FlexStack>
          <TextFieldFormItem
            name='secondaryContact.notes'
            label='Contact Instructions'
            control={control}
            multiline
            minRows={2}
            maxRows={10}
            fullWidth
            disabled={!!company && !company.isActive}
          />
        </Stack>
      </FlexCard>
      <FlexCard>
        <PrimaryCardHeader title='On Call' />
        <Stack useFlexGap sx={{ padding: 2, width: '100%' }}>
          <TextFieldFormItem
            name='onCallNotes'
            label='Instructions'
            control={control}
            multiline
            minRows={2}
            maxRows={10}
            fullWidth
            disabled={!!company && !company.isActive}
          />
          {!!company && (isServiceTitanPortalV2Company(company) || isStCapacityCompany(company)) && (
            <CheckboxFormItem
              name='isAfterHoursReviewEnabled'
              label='After Hours Review Enabled'
              control={control}
              disabled={!!company && !company.isActive}
            />
          )}
          <Stack spacing={0} useFlexGap>
            <TextFieldFormItem
              name='contactScheduleUrl'
              label='Contact Schedule URL'
              control={control}
              fullWidth
              disabled={!!company && !company.isActive}
            />
            {company?.isZoned && (
              <FormHelperText sx={{ paddingLeft: 1.75, marginTop: -0.5 }}>
                Schedule URL can be overwritten by individual zones
              </FormHelperText>
            )}
          </Stack>
        </Stack>
      </FlexCard>
    </Stack>
  );
};
